import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Send } from "lucide-react";
import { toast } from "sonner";
import { apiUrl } from "@/hooks/useApi";
import { useLang } from "@/i18n/LanguageContext";

const inputCls = "w-full bg-transparent border-b border-[#1C1B18]/20 focus:border-[#C5A059] outline-none py-3 text-sm text-[#1C1B18] placeholder:text-[#666157]/60 transition-colors duration-500";
const labelCls = "block text-[10px] tracking-[0.25em] uppercase text-[#666157] font-medium mb-1";

const ContactForm = ({ source = "contatti" }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", interestType: "", message: "", privacyConsent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const interests = t("form.interests");

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!form.privacyConsent) {
      toast.error(t("form.privacyError"));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(apiUrl("/contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, interestType: form.interestType || interests[5], source }),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      toast.success(t("form.successToast"));
    } catch {
      toast.error(t("form.error"));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-[#C5A059]/40 bg-[#C5A059]/5 p-10 text-center" data-testid="contact-form-success" role="status">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[#C5A059] text-[#9E7B36] mb-5">
          <Check size={20} />
        </span>
        <h3 className="font-serif-display text-2xl text-[#1C1B18]">{t("form.successTitle")}</h3>
        <p className="text-sm text-[#666157] mt-3 leading-relaxed">{t("form.successBody")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <div>
          <label htmlFor="cf-name" className={labelCls}>{t("form.name")} *</label>
          <input id="cf-name" data-testid="contact-form-name" required minLength={2} value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder={t("form.namePh")} />
        </div>
        <div>
          <label htmlFor="cf-org" className={labelCls}>{t("form.org")}</label>
          <input id="cf-org" data-testid="contact-form-organization" value={form.organization} onChange={(e) => set("organization", e.target.value)} className={inputCls} placeholder={t("form.optional")} />
        </div>
        <div>
          <label htmlFor="cf-email" className={labelCls}>{t("form.email")} *</label>
          <input id="cf-email" type="email" data-testid="contact-form-email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="nome@esempio.it" />
        </div>
        <div>
          <label htmlFor="cf-phone" className={labelCls}>{t("form.phone")}</label>
          <input id="cf-phone" type="tel" data-testid="contact-form-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder={t("form.optional")} />
        </div>
      </div>

      <div>
        <label htmlFor="cf-interest" className={labelCls}>{t("form.interest")} *</label>
        <select id="cf-interest" data-testid="contact-form-entity-type" required value={form.interestType} onChange={(e) => set("interestType", e.target.value)} className={`${inputCls} cursor-pointer`}>
          <option value="" disabled>{t("form.interestPlaceholder")}</option>
          {interests.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      <div>
        <label htmlFor="cf-message" className={labelCls}>{t("form.message")} *</label>
        <textarea id="cf-message" data-testid="contact-form-message" required minLength={10} rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className={`${inputCls} resize-none`} placeholder={t("form.messagePh")} />
      </div>

      <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="contact-form-privacy">
        <input type="checkbox" checked={form.privacyConsent} onChange={(e) => set("privacyConsent", e.target.checked)} className="mt-1 accent-[#9E7B36]" required />
        <span className="text-xs text-[#666157] leading-relaxed">{t("form.privacy")} *</span>
      </label>

      <button
        type="submit"
        disabled={sending}
        data-testid="contact-form-submit-button"
        className="inline-flex items-center gap-3 bg-[#1C1B18] text-[#F7F5F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#9E7B36] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-500"
      >
        {sending ? t("form.sending") : t("form.submit")} <Send size={13} />
      </button>
    </form>
  );
};

export default ContactForm;
